import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import {
  getRankingSequenceRecommendation,
  getWinnerConfidence,
  type ProfileAuditResponse,
  type ProfileAnalysisResponse,
  PROFILE_VERTICAL_CONFIGS,
  getRatingContextForVertical,
  type ProfileRewriteResponse,
  type ProfileRewriteTone,
  type ProjectProfileSectionId,
} from '@photofeeler/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStore } from '@/stores/user';
import { useGameStore } from '@/stores/game';
import { API_CONFIG } from '@/config';
import { getLevelDef, getLevelProgress } from '@/data/levels';
import { getPrimaryFocusAction } from '@/data/project-loops';
import { showToast } from '@/components/Toast';

interface PhotoAnalysis {
  id: string;
  context: string;
  status: string;
  created_at: string;
  aggregates: { composite_score: number; model_count: number }[];
}

const REWRITEABLE_SECTION_IDS = new Set<ProjectProfileSectionId>(['bio', 'headline', 'about', 'banner']);
const REWRITE_TONES: Array<{ value: ProfileRewriteTone; label: string }> = [
  { value: 'clearer', label: 'Clearer' },
  { value: 'warmer', label: 'Warmer' },
  { value: 'sharper', label: 'Sharper' },
  { value: 'confident', label: 'Confident' },
];

// --- Progressive disclosure ---
type WorkspaceStage = 'empty' | 'started' | 'progressing';

function getWorkspaceStage(project: { photoIds: string[]; currentScore: number | null; bestScore: number | null }): WorkspaceStage {
  const photoCount = project.photoIds?.length ?? 0;
  const hasCompletedAnalysis = project.currentScore != null || project.bestScore != null;

  if (photoCount === 0) return 'empty';
  if (!hasCompletedAnalysis) return 'started';
  return 'progressing';
}

// --- Collapsible section wrapper ---
function CollapsibleSection({
  title,
  subtitle,
  defaultOpen = false,
  children,
}: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-8">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between mb-3"
      >
        <div className="text-left">
          <h2 className="text-sm font-sans font-medium text-ink-muted">{title}</h2>
          {subtitle && (
            <p className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>
              {subtitle}
            </p>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-ink-faint transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        {children}
      </div>
    </div>
  );
}

// --- "How it works" card for empty stage ---
function HowItWorksCard({ onUploadClick }: { onUploadClick: () => void }) {
  const steps = [
    {
      number: '1',
      title: 'Upload a photo',
      description: 'AI scores it in about 15 seconds.',
    },
    {
      number: '2',
      title: 'See your score',
      description: 'Strengths, weaknesses, and what to improve.',
    },
    {
      number: '3',
      title: 'Retake and compare',
      description: 'Use the coaching tips, then compare versions.',
    },
  ];

  return (
    <div className="card p-6 sm:p-8 mb-8">
      <h2 className="font-serif text-2xl sm:text-3xl text-ink mb-2" style={{ fontWeight: 600 }}>
        How it works
      </h2>
      <p className="text-sm text-ink-muted mb-6" style={{ fontWeight: 300 }}>
        Three steps to a stronger photo and profile.
      </p>
      <div className="space-y-4">
        {steps.map(step => (
          <div key={step.number} className="flex items-start gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose/10 text-sm font-sans text-rose" style={{ fontWeight: 600 }}>
              {step.number}
            </div>
            <div>
              <div className="text-sm font-sans font-medium text-ink">{step.title}</div>
              <div className="text-xs text-ink-faint mt-0.5" style={{ fontWeight: 300 }}>
                {step.description}
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={onUploadClick}
        className="btn-rose text-xs px-6 py-2.5 mt-6"
      >
        Upload your first photo
      </button>
    </div>
  );
}

export function ScoreTab() {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const { credits } = useUserStore();
  const {
    xp,
    level,
    projects,
    activeProjectId,
    setActiveProject,
    completeProjectModule,
    updateProjectProfileSection,
    saveProjectProfileVersion,
    updateProjectOutfitNotes,
    applyProjectProfileAnalysis,
    applyProjectAudit,
    applyProjectRewrite,
    restoreProjectProfileVersion,
    outcomeLogs,
    retestLogs,
  } = useGameStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rewriteTones, setRewriteTones] = useState<Partial<Record<ProjectProfileSectionId, ProfileRewriteTone>>>({});
  const [analyzingSectionId, setAnalyzingSectionId] = useState<ProjectProfileSectionId | null>(null);
  const [rewritingSectionId, setRewritingSectionId] = useState<ProjectProfileSectionId | null>(null);
  const [auditingProjectId, setAuditingProjectId] = useState<string | null>(null);

  const activeProject =
    projects.find(project => project.id === activeProjectId) ||
    projects.find(project => project.vertical === 'dating') ||
    projects[0];

  const stage: WorkspaceStage = activeProject ? getWorkspaceStage(activeProject) : 'empty';
  const activeVertical = activeProject?.vertical || 'dating';
  const activeConfig = PROFILE_VERTICAL_CONFIGS[activeVertical];
  const activeRatingContext = getRatingContextForVertical(activeVertical);
  const projectOutcomeLogs = outcomeLogs.filter(log => log.projectId === activeProject?.id);
  const projectRetests = retestLogs.filter(log => log.projectId === activeProject?.id);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && activeProject) {
      navigate('/upload', {
        state: { file, preselectedVertical: activeProject.vertical },
      });
    }
  };

  const levelDef = getLevelDef(level);
  const progress = getLevelProgress(xp);
  const nextLevelDef = getLevelDef(level + 1);

  const { data, isLoading } = useQuery({
    queryKey: ['photos', user?.id],
    queryFn: async () => {
      if (!session?.access_token) return { photos: [] };
      const res = await fetch(`${API_CONFIG.RATE_URL}/photos`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return { photos: [] };
      return res.json() as Promise<{ photos: PhotoAnalysis[] }>;
    },
    enabled: !!session?.access_token,
  });

  const allPhotos = data?.photos || [];
  const projectPhotos = allPhotos.filter(
    photo => photo.context === activeRatingContext && photo.status === 'completed'
  );
  const completedScores = projectPhotos
    .map(photo => photo.aggregates?.[0]?.composite_score)
    .filter((score): score is number => score != null);
  const averageScore = completedScores.length > 0
    ? Math.round(completedScores.reduce((sum, score) => sum + score, 0) / completedScores.length)
    : null;
  const latestRetest = projectRetests.at(-1);
  const latestOutcome = projectOutcomeLogs.at(-1);
  const compareHistory = [...(activeProject?.compareRuns || [])]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3);

  // --- Prompt 4: "Rank your photos" ---
  const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
  const completedPhotoCount = projectPhotos.length; // already filtered to status === 'completed'
  const mostRecentCompareRun = compareHistory[0]; // sorted desc above
  const hasRecentCompare = mostRecentCompareRun
    ? Date.now() - new Date(mostRecentCompareRun.createdAt).getTime() < FOURTEEN_DAYS_MS
    : false;
  const showRankPrompt = completedPhotoCount >= 3 && !hasRecentCompare;

  // --- Prompt 5: "Time to retest" ---
  const mostRecentPhoto = projectPhotos.length > 0
    ? projectPhotos.reduce((latest, photo) =>
        new Date(photo.created_at).getTime() > new Date(latest.created_at).getTime() ? photo : latest
      )
    : null;
  const daysSinceLastTest = mostRecentPhoto
    ? Math.floor((Date.now() - new Date(mostRecentPhoto.created_at).getTime()) / (24 * 60 * 60 * 1000))
    : null;
  const showRetestPrompt = !showRankPrompt && daysSinceLastTest != null && daysSinceLastTest >= 30;
  const filledProfileSections = activeProject
    ? activeProject.profileWorkspace.sections.filter(section => section.value.trim().length > 0).length
    : 0;
  const activePolishChecklist = activeProject?.profileWorkspace.polishChecklist || [];
  const activeAudit = activeProject?.profileWorkspace.audit;
  const primaryFocusAction = activeProject ? getPrimaryFocusAction(activeProject) : null;

  const scoreColor = (score: number) =>
    score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-blue-600' : score >= 40 ? 'text-amber-600' : 'text-rose';

  const fmtDate = (isoDate: string) => {
    const hours = Math.floor((Date.now() - new Date(isoDate).getTime()) / 3600000);
    return hours < 1 ? 'Just now' : hours < 24 ? `${hours}h ago` : hours < 48 ? 'Yesterday' : `${Math.floor(hours / 24)}d ago`;
  };

  const scrollToSection = (id: string) => {
    if (typeof document === 'undefined') return;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePrimaryFocus = () => {
    if (!activeProject || !primaryFocusAction) return;

    if (primaryFocusAction.id === 'upload-lineup') {
      fileInputRef.current?.click();
      return;
    }

    if (primaryFocusAction.id === 'pick-lead') {
      navigate(`/compare?vertical=${activeProject.vertical}`);
      return;
    }

    if (primaryFocusAction.id === 'tighten-profile') {
      scrollToSection('profile-planner-section');
      return;
    }

    if (primaryFocusAction.id === 'outfit-check') {
      scrollToSection('outfit-review-section');
      return;
    }

    if (primaryFocusAction.id === 'log-results') {
      navigate('/app?tab=progress');
    }
  };

  const primaryFocusLabel = (() => {
    if (!primaryFocusAction || !activeProject) return 'Continue';
    if (primaryFocusAction.id === 'upload-lineup') return `Upload to ${activeConfig.shortLabel}`;
    if (primaryFocusAction.id === 'pick-lead') {
      return activeProject.vertical === 'linkedin' ? 'Compare headshots' : 'Compare dating photos';
    }
    if (primaryFocusAction.id === 'tighten-profile') return 'Open profile planner';
    if (primaryFocusAction.id === 'outfit-check') return 'Review outfit';
    if (primaryFocusAction.id === 'log-results') return 'Log results';
    return 'Continue';
  })();

  const handleSaveDraft = (sectionId: ProjectProfileSectionId, sectionLabel: string) => {
    if (!activeProject) return;
    saveProjectProfileVersion(activeProject.id, sectionId, 'Manual draft');
    showToast(`${sectionLabel} draft saved.`, 'success');
  };

  const getPreferredRewriteTone = (sectionId: ProjectProfileSectionId) => {
    const section = activeProject?.profileWorkspace.sections.find(item => item.id === sectionId);
    return rewriteTones[sectionId] || section?.analysis?.recommendedTone || 'clearer';
  };

  const handleAnalyze = async (sectionId: ProjectProfileSectionId, options?: { silent?: boolean; currentValue?: string }) => {
    if (!activeProject || !session?.access_token) return;
    const section = activeProject.profileWorkspace.sections.find(item => item.id === sectionId);
    const currentValue = options?.currentValue ?? section?.value ?? '';

    if (!section || !currentValue.trim()) {
      if (!options?.silent) {
        showToast(`Add some text to ${section?.label.toLowerCase() || 'this section'} first.`, 'error');
      }
      return;
    }

    setAnalyzingSectionId(sectionId);
    try {
      const res = await fetch(`${API_CONFIG.RATE_URL}/user/profile-analysis`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vertical: activeProject.vertical,
          sectionId,
          currentValue,
          goal: activeProject.goal,
          suggestions: activeProject.profileWorkspace.suggestions.slice(0, 5),
          outfitSummary: activeProject.outfitWorkspace.review?.summary,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to analyze profile section');
      }

      const analysis = await res.json() as ProfileAnalysisResponse;
      applyProjectProfileAnalysis(activeProject.id, sectionId, analysis);

      if (!options?.silent && !rewriteTones[sectionId]) {
        setRewriteTones(current => ({
          ...current,
          [sectionId]: analysis.recommendedTone,
        }));
      }

      if (!options?.silent) {
        showToast(`${section.label} analysis ready.`, 'success');
      }
    } catch {
      if (!options?.silent) {
        showToast(`We couldn't analyze your ${section.label.toLowerCase()} right now.`, 'error');
      }
    } finally {
      setAnalyzingSectionId(null);
    }
  };

  const handleRewrite = async (sectionId: ProjectProfileSectionId) => {
    if (!activeProject || !session?.access_token) return;
    const section = activeProject.profileWorkspace.sections.find(item => item.id === sectionId);
    if (!section) return;

    setRewritingSectionId(sectionId);
    try {
      const res = await fetch(`${API_CONFIG.RATE_URL}/user/profile-rewrite`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vertical: activeProject.vertical,
          sectionId,
          tone: getPreferredRewriteTone(sectionId),
          currentValue: section.value,
          goal: activeProject.goal,
          suggestions: activeProject.profileWorkspace.suggestions.slice(0, 5),
          outfitSummary: activeProject.outfitWorkspace.review?.summary,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to generate rewrite');
      }

      const rewrite = await res.json() as ProfileRewriteResponse;
      applyProjectRewrite(activeProject.id, sectionId, rewrite);
      await handleAnalyze(sectionId, { silent: true, currentValue: rewrite.content });
      showToast(`${section.label} rewrite ready.`, 'success');
    } catch {
      showToast(`We couldn't rewrite your ${section.label.toLowerCase()} right now.`, 'error');
    } finally {
      setRewritingSectionId(null);
    }
  };

  const handleRunProfileAudit = async () => {
    if (!activeProject || !session?.access_token) return;

    setAuditingProjectId(activeProject.id);
    try {
      const res = await fetch(`${API_CONFIG.RATE_URL}/user/profile-audit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vertical: activeProject.vertical,
          goal: activeProject.goal,
          currentScore: activeProject.currentScore,
          bestScore: activeProject.bestScore,
          profileSections: activeProject.profileWorkspace.sections.map(section => ({
            id: section.id,
            label: section.label,
            value: section.value,
          })),
          profileSuggestions: activeProject.profileWorkspace.suggestions.slice(0, 5),
          outfitSummary:
            activeProject.outfitWorkspace.review?.summary || activeProject.outfitWorkspace.notes || undefined,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to run profile audit');
      }

      const audit = (await res.json()) as ProfileAuditResponse;
      applyProjectAudit(activeProject.id, audit);
      setRewriteTones(current => {
        const next = { ...current };
        audit.suggestedRewriteTargets.forEach(target => {
          if (!next[target.sectionId]) {
            next[target.sectionId] = target.tone;
          }
        });
        return next;
      });
      showToast('Full profile audit ready.', 'success');
    } catch {
      showToast(`We couldn't run the ${activeConfig.shortLabel} audit right now.`, 'error');
    } finally {
      setAuditingProjectId(null);
    }
  };

  return (
    <div className="pb-32 pt-8 px-4 sm:px-6 max-w-6xl mx-auto animate-fade-in">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      <div className="mb-6 max-w-3xl">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-sans text-ink-faint" style={{ fontWeight: 300 }}>
            {levelDef.emoji} Level {level}: {levelDef.name}
          </span>
          <span className="text-xs font-sans text-ink-faint" style={{ fontWeight: 300 }}>
            {xp} / {nextLevelDef.xpRequired > 0 ? nextLevelDef.xpRequired : '---'} XP
          </span>
        </div>
        <div className="w-full h-1.5 bg-surface-border rounded-full overflow-hidden">
          <div
            className="h-full bg-success rounded-full transition-all duration-500"
            style={{ width: `${Math.min(progress * 100, 100)}%` }}
          />
        </div>
      </div>

      <div className="marketing-panel-warm p-6 sm:p-8 mb-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="section-kicker !text-left !mb-3">Projects</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink leading-tight mb-3" style={{ fontWeight: 600 }}>
              Build better Dating and LinkedIn profiles, not one-off scores.
            </h1>
            <p className="text-base text-ink-muted leading-relaxed">
              Use the workspace to choose the winner, tighten the written profile, review outfit cues, and come back with a better next version.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="metric-tile min-w-[140px]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-ink-faint mb-2">Workspace</div>
              <div className="text-lg font-sans text-ink" style={{ fontWeight: 600 }}>{activeConfig.shortLabel}</div>
              <div className="text-xs text-ink-faint mt-1">Current focus ready</div>
            </div>
            <div className="metric-tile min-w-[140px]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-ink-faint mb-2">Credits</div>
              <div className="text-lg font-sans text-ink" style={{ fontWeight: 600 }}>{credits}</div>
              <div className="text-xs text-ink-faint mt-1">Ready for another upload</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-6">
          <div className="text-xs text-ink-faint">
            {stage === 'empty'
              ? 'Get started with your first photo analysis.'
              : stage === 'started'
                ? 'Your photos are uploaded. Analysis results are on the way.'
                : 'Keep moving one module forward at a time.'}
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-rose text-xs px-4 py-2"
          >
            {stage === 'empty' ? 'Upload your first photo' : `Upload to ${activeConfig.shortLabel}`}
          </button>
        </div>
      </div>

      {stage === 'empty' && (
        <HowItWorksCard onUploadClick={() => fileInputRef.current?.click()} />
      )}

      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="app-section-heading">
            Projects
          </h2>
          <span className="text-[11px] text-ink-faint">
            Switch between Dating and LinkedIn
          </span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-2 mb-8">
        {projects.map(project => {
          const config = PROFILE_VERTICAL_CONFIGS[project.vertical];
          const isActive = project.id === activeProject?.id;
          const openActionCount = project.nextActions.filter(action => !action.completed).length;
          return (
            <button
              key={project.id}
              onClick={() => setActiveProject(project.id)}
              className={`card-hover text-left p-5 ${isActive ? 'ring-1 ring-rose/40 bg-rose-faint' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg">{config.emoji}</span>
                <span className="text-[10px] font-sans uppercase tracking-wider text-ink-faint">
                  {project.status}
                </span>
              </div>
              <div className="font-serif text-2xl text-ink mb-1" style={{ fontWeight: 600 }}>{project.title}</div>
              <div className="text-[11px] text-ink-muted mb-2" style={{ fontWeight: 400 }}>
                {config.workspaceTagline}
              </div>
              <div className="text-[11px] text-ink-faint mb-3" style={{ fontWeight: 300 }}>
                {project.currentScore != null
                  ? `Current score ${project.currentScore}`
                  : 'No completed analysis yet'}
              </div>
              <div className="flex items-center justify-between text-[11px] text-ink-faint">
                <span>{project.photoIds.length} photo{project.photoIds.length === 1 ? '' : 's'}</span>
                <span>{openActionCount} next step{openActionCount === 1 ? '' : 's'}</span>
              </div>
            </button>
          );
        })}
      </div>

      {activeProject && (
        <>
          <div className="card-rose mb-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-1">
                  {activeConfig.shortLabel} workspace
                </div>
                <h2 className="font-serif text-3xl text-ink mb-2" style={{ fontWeight: 600 }}>
                  {activeProject.title}
                </h2>
                <p className="text-sm text-ink-muted leading-relaxed" style={{ fontWeight: 300 }}>
                  {activeProject.summary}
                </p>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-sans uppercase tracking-wider text-ink-faint mb-1">
                  Current score
                </div>
                <div
                  className={`text-3xl font-sans ${activeProject.currentScore != null ? scoreColor(activeProject.currentScore) : 'text-ink-faint'}`}
                  style={{ fontWeight: 600 }}
                >
                  {activeProject.currentScore ?? '--'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'Photos', value: activeProject.photoIds.length },
                { label: 'Best', value: activeProject.bestScore ?? '--' },
                { label: 'Retests', value: projectRetests.length },
                { label: 'Outcomes', value: projectOutcomeLogs.length },
              ].map(stat => (
                <div key={stat.label} className="rounded-xl border border-surface-border bg-surface p-3 text-center">
                  <div className="text-base font-sans text-ink" style={{ fontWeight: 600 }}>
                    {stat.value}
                  </div>
                  <div className="text-[10px] text-ink-faint">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {showRankPrompt && (
            <div className="card-rose mb-8 p-5">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose/10 text-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5 text-rose"
                  >
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C7 4 9 7 12 7s5-3 7.5-3a2.5 2.5 0 0 1 0 5H18" />
                    <path d="M18 9v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9" />
                    <path d="M12 7v15" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-ink mb-1" style={{ fontWeight: 600 }}>
                    Rank your photos
                  </div>
                  <p className="text-sm text-ink-muted leading-relaxed mb-3" style={{ fontWeight: 300 }}>
                    You have {completedPhotoCount} scored {activeVertical === 'linkedin' ? 'LinkedIn' : 'dating'} photos.
                    Rank them to find your winner and get lineup recommendations.
                  </p>
                  <button
                    onClick={() => navigate(`/compare?vertical=${activeProject.vertical}`)}
                    className="btn-rose text-xs px-4 py-2"
                  >
                    Rank my photos
                  </button>
                </div>
              </div>
            </div>
          )}

          {showRetestPrompt && daysSinceLastTest != null && (
            <div className="rounded-xl border border-surface-border bg-cream-warm px-4 py-3 mb-8 flex items-center justify-between gap-4">
              <p className="text-xs text-ink-muted leading-relaxed" style={{ fontWeight: 300 }}>
                It&apos;s been {daysSinceLastTest} day{daysSinceLastTest === 1 ? '' : 's'} since your last photo test.
                Profiles that update regularly get more engagement.
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn-secondary text-xs whitespace-nowrap px-3 py-1.5"
              >
                Upload a new photo
              </button>
            </div>
          )}

          {primaryFocusAction && (
            <div id="current-focus-section" className="marketing-panel p-5 mb-8">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h2 className="text-sm font-sans font-medium text-ink-muted">
                    Current focus
                  </h2>
                  <div className="font-serif text-3xl text-ink mt-2" style={{ fontWeight: 600 }}>
                    {primaryFocusAction.title}
                  </div>
                  <div className="text-sm text-ink-muted mt-2 max-w-2xl" style={{ fontWeight: 300 }}>
                    {primaryFocusAction.detail}
                  </div>
                  {activeProject?.vertical === 'dating' && (
                    <Link to="/guides/hinge" className="text-xs text-accent hover:text-accent-strong mt-2 inline-block">
                      Read: How to choose the best dating photos →
                    </Link>
                  )}
                  {activeProject?.vertical === 'linkedin' && (
                    <Link to="/guides/linkedin" className="text-xs text-accent hover:text-accent-strong mt-2 inline-block">
                      Read: How to choose the best LinkedIn headshot →
                    </Link>
                  )}
                </div>
                <span className="hero-chip py-1.5 px-3 text-[10px] uppercase tracking-[0.22em]">
                  {primaryFocusAction.priority}
                </span>
              </div>
              <button onClick={handlePrimaryFocus} className="btn-secondary text-xs px-4 py-2">
                {primaryFocusLabel}
              </button>
            </div>
          )}

          {stage !== 'empty' && (
            <CollapsibleSection
              title="Workspace modules"
              subtitle="Keep moving each module forward."
              defaultOpen={stage === 'progressing'}
            >
              <div className="space-y-3">
                {activeProject.modules.map(module => (
                  <div key={module.id} className="card p-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="text-sm font-sans font-medium text-ink">{module.label}</div>
                        <div className="text-xs text-ink-faint mt-0.5" style={{ fontWeight: 300 }}>
                          {module.description}
                        </div>
                      </div>
                      <span
                        className={`text-[10px] font-sans uppercase tracking-wider ${
                          module.status === 'completed'
                            ? 'text-emerald-600'
                            : module.status === 'in_progress'
                              ? 'text-blue-600'
                              : 'text-ink-faint'
                        }`}
                      >
                        {module.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-surface-border rounded-full overflow-hidden mb-3">
                      <div
                        className={`h-full rounded-full ${
                          module.status === 'completed'
                            ? 'bg-emerald-500'
                            : module.status === 'in_progress'
                              ? 'bg-blue-500'
                              : 'bg-ink/10'
                        }`}
                        style={{ width: `${module.completion}%` }}
                      />
                    </div>
                    {module.id !== 'photos' && module.id !== 'results' && module.status !== 'completed' && (
                      <button
                        onClick={() => completeProjectModule(activeProject.id, module.id)}
                        className="btn-secondary text-xs"
                      >
                        Mark {module.label.toLowerCase()} complete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {stage === 'progressing' && (
            <CollapsibleSection
              title="Full profile audit"
              subtitle="Run one workspace-wide pass to find the biggest blockers, missing pieces, and best next moves."
              defaultOpen={false}
            >
            <div className="card p-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <p className="text-xs text-ink-faint mt-1" style={{ fontWeight: 300 }}>
                    Analyze all sections together for cross-cutting recommendations.
                  </p>
                </div>
                <button
                  onClick={handleRunProfileAudit}
                  disabled={auditingProjectId === activeProject.id}
                  className="btn-secondary text-[11px]"
                >
                  {auditingProjectId === activeProject.id ? 'Running audit…' : `Run ${activeConfig.shortLabel} profile audit`}
                </button>
              </div>

              {activeAudit ? (
                <div className="space-y-4">
                  <div className="rounded-xl bg-cream-warm border border-surface-border p-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase">
                        Full profile audit
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-ink-faint">
                        {activeAudit.signal.replace('_', ' ')}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`text-3xl font-sans ${scoreColor(activeAudit.overallScore)}`} style={{ fontWeight: 600 }}>
                        {activeAudit.overallScore}
                      </div>
                      <div className="text-xs text-ink-muted" style={{ fontWeight: 300 }}>
                        {activeAudit.summary}
                      </div>
                    </div>
                    <div className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>
                      {activeAudit.consistencySummary}
                    </div>
                  </div>

                  {activeAudit.strengths.length > 0 && (
                    <div className="rounded-xl bg-cream-warm border border-surface-border p-3">
                      <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                        What already works
                      </div>
                      <div className="space-y-1">
                        {activeAudit.strengths.map(item => (
                          <div key={item} className="text-xs text-ink-muted" style={{ fontWeight: 300 }}>
                            • {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    {activeAudit.sectionSummaries.map(section => (
                      <div key={`${section.area}-${section.label}`} className="rounded-xl border border-surface-border bg-surface p-3">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <div className="text-xs font-sans font-medium text-ink">{section.label}</div>
                          <div className={`text-base font-sans ${scoreColor(section.score)}`} style={{ fontWeight: 600 }}>
                            {section.score}
                          </div>
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-faint mb-1">
                          {section.signal.replace('_', ' ')}
                        </div>
                        <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                          {section.summary}
                        </div>
                      </div>
                    ))}
                  </div>

                  {activeAudit.topBlockers.length > 0 && (
                    <div className="rounded-xl bg-cream-warm border border-surface-border p-3">
                      <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                        Top blockers
                      </div>
                      <div className="space-y-2">
                        {activeAudit.topBlockers.map(blocker => (
                          <div key={`${blocker.area}-${blocker.title}`} className="rounded-lg border border-surface-border bg-surface p-3">
                            <div className="flex items-center justify-between gap-3 mb-1">
                              <div className="text-xs font-sans font-medium text-ink">{blocker.title}</div>
                              <div className="text-[10px] uppercase tracking-wider text-ink-faint">{blocker.priority}</div>
                            </div>
                            <div className="text-[11px] text-ink-faint mb-1" style={{ fontWeight: 300 }}>
                              {blocker.detail}
                            </div>
                            <div className="text-xs text-ink">
                              → {blocker.recommendation}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeAudit.missingAssets.length > 0 && (
                    <div className="rounded-xl bg-cream-warm border border-surface-border p-3">
                      <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                        Missing or underdeveloped
                      </div>
                      <div className="space-y-2">
                        {activeAudit.missingAssets.map(asset => (
                          <div key={`${asset.area}-${asset.label}`} className="text-xs text-ink-muted" style={{ fontWeight: 300 }}>
                            • <span className="font-medium text-ink">{asset.label}:</span> {asset.detail}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeAudit.nextActions.length > 0 && (
                    <div className="rounded-xl bg-cream-warm border border-surface-border p-3">
                      <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                        Do this next
                      </div>
                      <div className="space-y-2">
                        {activeAudit.nextActions.map(action => (
                          <div key={action.id} className="rounded-lg border border-surface-border bg-surface p-3">
                            <div className="flex items-center justify-between gap-3 mb-1">
                              <div className="text-xs font-sans font-medium text-ink">{action.title}</div>
                              <div className="text-[10px] uppercase tracking-wider text-ink-faint">{action.priority}</div>
                            </div>
                            <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                              {action.detail}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeAudit.suggestedRewriteTargets.length > 0 && (
                    <div className="rounded-xl bg-cream-warm border border-surface-border p-3">
                      <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                        Recommended rewrite targets
                      </div>
                      <div className="space-y-2">
                        {activeAudit.suggestedRewriteTargets.map(target => {
                          const section = activeProject.profileWorkspace.sections.find(item => item.id === target.sectionId);
                          return (
                            <div key={`${target.sectionId}-${target.tone}`} className="rounded-lg border border-surface-border bg-surface p-3">
                              <div className="flex items-center justify-between gap-3 mb-1">
                                <div className="text-xs font-sans font-medium text-ink">
                                  {section?.label || target.sectionId}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-faint">
                                  {target.tone}
                                </div>
                              </div>
                              <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                                {target.reason}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-xl bg-cream-warm border border-surface-border p-3 text-xs text-ink-muted" style={{ fontWeight: 300 }}>
                  No workspace-wide audit yet. Run one after you have at least a few profile sections drafted and a clear photo direction.
                </div>
              )}
            </div>
            </CollapsibleSection>
          )}

          {stage === 'progressing' && (
          <div id="profile-planner-section">
          <CollapsibleSection
            title="Profile planner"
            subtitle={`${filledProfileSections}/${activeProject.profileWorkspace.sections.length} sections drafted`}
            defaultOpen={false}
          >
            <div className="card p-4">
              {activePolishChecklist.length > 0 && (
                <div className="rounded-xl bg-cream-warm border border-surface-border p-3 mb-4">
                  <h3 className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                    Premium polish
                  </h3>
                  <div className="space-y-2">
                    {activePolishChecklist.map(item => (
                      <div key={item} className="text-xs text-ink-muted" style={{ fontWeight: 300 }}>
                        • {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeProject.profileWorkspace.suggestions.length > 0 && (
                <div className="rounded-xl bg-cream-warm border border-surface-border p-3 mb-4">
                  <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                    AI suggestions
                  </div>
                  <div className="space-y-2">
                    {activeProject.profileWorkspace.suggestions.map(suggestion => (
                      <div key={suggestion} className="text-xs text-ink-muted" style={{ fontWeight: 300 }}>
                        • {suggestion}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {activeProject.profileWorkspace.sections.map(section => {
                  const latestRewrite = section.draftVersions.find(version => version.source === 'ai_rewrite');
                  const canRewrite = REWRITEABLE_SECTION_IDS.has(section.id);
                  const preferredTone = getPreferredRewriteTone(section.id);

                  return (
                    <label key={section.id} className="block">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-sans font-medium text-ink">{section.label}</span>
                        <span className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                          {section.value.trim().length} chars
                        </span>
                      </div>
                      <div className="text-[11px] text-ink-faint mb-2" style={{ fontWeight: 300 }}>
                        {section.helper}
                      </div>
                      <textarea
                        aria-label={section.label}
                        value={section.value}
                        onChange={event =>
                          updateProjectProfileSection(activeProject.id, section.id, event.target.value)
                        }
                        placeholder={section.placeholder}
                        rows={section.id === 'about' ? 5 : 3}
                        className="w-full rounded-xl border border-surface-border bg-surface px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint/70 focus:outline-none focus:ring-2 focus:ring-rose/20"
                      />

                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => handleSaveDraft(section.id, section.label)}
                          className="btn-secondary text-[11px]"
                        >
                          Save {section.label} draft
                        </button>
                        {canRewrite && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleAnalyze(section.id)}
                              disabled={analyzingSectionId === section.id}
                              className="btn-secondary text-[11px]"
                            >
                              {analyzingSectionId === section.id ? 'Analyzing…' : `Analyze ${section.label}`}
                            </button>
                            <select
                              aria-label={`${section.label} rewrite tone`}
                              value={preferredTone}
                              onChange={event =>
                                setRewriteTones(current => ({
                                  ...current,
                                  [section.id]: event.target.value as ProfileRewriteTone,
                                }))
                              }
                              className="rounded-xl border border-surface-border bg-surface px-3 py-2 text-[11px] text-ink focus:outline-none focus:ring-2 focus:ring-rose/20"
                            >
                              {REWRITE_TONES.map(tone => (
                                <option key={tone.value} value={tone.value}>
                                  {tone.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleRewrite(section.id)}
                              disabled={rewritingSectionId === section.id}
                              className="btn-rose text-[11px] px-3 py-2"
                            >
                              {rewritingSectionId === section.id ? 'Rewriting…' : `Rewrite ${section.label}`}
                            </button>
                          </>
                        )}
                      </div>

                      {section.analysis && (
                        <div className="rounded-xl bg-cream-warm border border-surface-border p-3 mt-3">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase">
                              Copy analysis
                            </div>
                            <div className="text-[11px] font-sans text-ink" style={{ fontWeight: 600 }}>
                              Recommended tone: {section.analysis.recommendedTone}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`text-2xl font-sans ${scoreColor(section.analysis.score)}`} style={{ fontWeight: 600 }}>
                              {section.analysis.score}
                            </div>
                            <div className="text-[11px] uppercase tracking-wider text-ink-faint">
                              {section.analysis.signal.replace('_', ' ')}
                            </div>
                          </div>
                          <div className="text-xs text-ink-muted mb-3" style={{ fontWeight: 300 }}>
                            {section.analysis.summary}
                          </div>
                          {section.analysis.strengths.length > 0 && (
                            <div className="mb-3">
                              <div className="text-[10px] uppercase tracking-wider text-ink-faint mb-1">
                                What works
                              </div>
                              <div className="space-y-1">
                                {section.analysis.strengths.map(item => (
                                  <div key={item} className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                                    • {item}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {section.analysis.issues.length > 0 && (
                            <div className="mb-3 space-y-2">
                              {section.analysis.issues.map(issue => (
                                <div key={`${issue.issue}-${issue.suggestion}`} className="rounded-lg border border-surface-border bg-surface p-3">
                                  <div className="flex items-center justify-between gap-3 mb-1">
                                    <div className="text-xs font-sans font-medium text-ink">{issue.issue}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-ink-faint">{issue.priority}</div>
                                  </div>
                                  <div className="text-[11px] text-ink-faint mb-1" style={{ fontWeight: 300 }}>
                                    {issue.detail}
                                  </div>
                                  <div className="text-xs text-ink">
                                    → {issue.suggestion}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="text-[11px] text-ink-muted" style={{ fontWeight: 300 }}>
                            {section.analysis.rewriteRecommendation}
                          </div>
                        </div>
                      )}

                      {latestRewrite && (
                        <div className="rounded-xl bg-cream-warm border border-surface-border p-3 mt-3">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase">
                              Latest AI rewrite
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-ink-faint">
                              {latestRewrite.tone}
                            </div>
                          </div>
                          {latestRewrite.rationale && (
                            <div className="text-xs text-ink-muted mb-2" style={{ fontWeight: 300 }}>
                              {latestRewrite.rationale}
                            </div>
                          )}
                          {latestRewrite.highlights.length > 0 && (
                            <div className="space-y-1">
                              {latestRewrite.highlights.map(item => (
                                <div key={item} className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                                  • {item}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {section.draftVersions.length > 0 && (
                        <div className="rounded-xl border border-surface-border bg-surface p-3 mt-3">
                          <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                            Draft history
                          </div>
                          <div className="space-y-2">
                            {section.draftVersions.slice(0, 3).map((version, index) => (
                              <div
                                key={version.id}
                                className="flex items-center justify-between gap-3 rounded-lg bg-cream-warm px-3 py-2"
                              >
                                <div className="min-w-0">
                                  <div className="text-xs font-sans font-medium text-ink">
                                    {version.label}
                                  </div>
                                  <div className="text-[11px] text-ink-faint truncate" style={{ fontWeight: 300 }}>
                                    {version.content}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => restoreProjectProfileVersion(activeProject.id, section.id, version.id)}
                                  className="btn-ghost text-[11px]"
                                  aria-label={`Restore ${section.label} draft ${index + 1}`}
                                >
                                  Restore
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          </CollapsibleSection>
          </div>
          )}

          {stage === 'progressing' && (
          <div id="outfit-review-section">
          <CollapsibleSection
            title="Outfit review"
            subtitle={activeProject.outfitWorkspace.review ? 'AI review saved' : 'Add notes or analyze a photo'}
            defaultOpen={false}
          >
            <div className="card p-4">
              {activeProject.outfitWorkspace.review ? (
                <div className="rounded-xl bg-cream-warm border border-surface-border p-3 mb-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-sm font-sans font-medium text-ink">
                      Outfit signal
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-faint">
                      {activeProject.outfitWorkspace.review.signal.replace('_', ' ')}
                    </div>
                  </div>
                  <div className="text-xs text-ink-muted mb-3" style={{ fontWeight: 300 }}>
                    {activeProject.outfitWorkspace.review.summary}
                  </div>
                  <div className="space-y-2">
                    {activeProject.outfitWorkspace.review.recommendations.map(recommendation => (
                      <div key={`${recommendation.title}-${recommendation.suggestion}`} className="rounded-lg border border-surface-border bg-surface p-3">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <div className="text-sm font-sans font-medium text-ink">
                            {recommendation.title}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-ink-faint">
                            {recommendation.priority}
                          </div>
                        </div>
                        <div className="text-[11px] text-ink-faint mb-1" style={{ fontWeight: 300 }}>
                          {recommendation.detail}
                        </div>
                        <div className="text-xs text-ink">
                          → {recommendation.suggestion}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl bg-cream-warm border border-surface-border p-3 mb-4 text-xs text-ink-muted" style={{ fontWeight: 300 }}>
                  Your outfit review will fill automatically after new analyses. You can also keep manual notes here for the next reshoot.
                </div>
              )}

              <label className="block">
                <div className="text-sm font-sans font-medium text-ink mb-1.5">Reshoot notes</div>
                <textarea
                  value={activeProject.outfitWorkspace.notes}
                  onChange={event => updateProjectOutfitNotes(activeProject.id, event.target.value)}
                  placeholder={
                    activeProject.vertical === 'dating'
                      ? 'Example: keep the jacket, switch to a lighter tee, avoid the busy bar background.'
                      : 'Example: keep the blazer, use a solid shirt, and simplify the background.'
                  }
                  rows={4}
                  className="w-full rounded-xl border border-surface-border bg-surface px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint/70 focus:outline-none focus:ring-2 focus:ring-rose/20"
                />
              </label>
            </div>
          </CollapsibleSection>
          </div>
          )}

          {stage === 'progressing' && (
          <CollapsibleSection
            title="Compare history"
            subtitle="Side-by-side photo comparisons and rankings."
            defaultOpen={false}
          >
            <div className="flex items-center justify-end mb-3">
              <button
                onClick={() => navigate(`/compare?vertical=${activeProject.vertical}`)}
                className="text-xs font-sans font-medium text-rose hover:text-rose-dark transition-colors"
              >
                New compare run
              </button>
            </div>

            {compareHistory.length > 0 ? (
              <div className="space-y-3">
                {compareHistory.map(run => (
                  <div key={run.id} className="card">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="text-sm font-sans font-medium text-ink">
                          Winner {run.winnerScore != null ? `${run.winnerScore}` : '--'}
                        </div>
                        <div className="text-xs text-ink-faint mt-1" style={{ fontWeight: 300 }}>
                          {run.rankings.length} ranked photo{run.rankings.length === 1 ? '' : 's'} · {fmtDate(run.createdAt)}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(run.winnerPhotoId ? `/results/${run.winnerPhotoId}` : `/compare-details?project=${activeProject.id}`)}
                        className="btn-ghost text-[11px]"
                      >
                        View winner
                      </button>
                    </div>
                    {(() => {
                      const confidence = getWinnerConfidence(run.rankings);
                      const sequence = getRankingSequenceRecommendation(activeProject.vertical, run.rankings);
                      return (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`rounded-full px-2.5 py-1 text-[10px] font-sans ${
                              confidence.level === 'high'
                                ? 'bg-emerald-50 text-emerald-700'
                                : confidence.level === 'medium'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-amber-50 text-amber-700'
                            }`}>
                              {confidence.label}
                            </span>
                            <span className="text-[10px] text-ink-faint">Gap {confidence.scoreGap}</span>
                          </div>
                          {sequence ? (
                            <div className="space-y-2">
                              {sequence.items.slice(0, activeProject.vertical === 'linkedin' ? 2 : 3).map(item => (
                                <div key={item.photoId} className="flex items-start gap-3 rounded-xl border border-surface-border bg-cream-warm px-3 py-2">
                                  <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[10px] font-sans text-ink">
                                    {item.position}
                                  </div>
                                  <div>
                                    <div className="text-xs font-sans text-ink" style={{ fontWeight: 600 }}>
                                      {item.title}
                                    </div>
                                    <div className="text-[10px] text-ink-faint mt-0.5" style={{ fontWeight: 300 }}>
                                      {item.rationale}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : (
              <div className="card text-center py-8">
                <div className="text-3xl mb-3">🏆</div>
                <p className="text-sm font-sans text-ink mb-1" style={{ fontWeight: 500 }}>
                  No compare runs yet
                </p>
                <p className="text-xs text-ink-faint mb-4" style={{ fontWeight: 300 }}>
                  Rank a full set to pick a clearer lead photo for this workspace.
                </p>
                <button
                  onClick={() => navigate(`/compare?vertical=${activeProject.vertical}`)}
                  className="btn-secondary text-xs"
                >
                  Start compare run
                </button>
              </div>
            )}
          </CollapsibleSection>
          )}

          <div className="mb-8">
            <h2 className="text-sm font-sans font-medium text-ink-muted mb-3">
              Next actions
            </h2>
            <div className="space-y-3">
              {activeProject.nextActions.map(action => (
                <div
                  key={action.id}
                  className={`card flex items-start gap-3 ${action.completed ? 'opacity-70' : ''}`}
                >
                  <div className={`mt-0.5 text-xs ${action.completed ? 'text-emerald-500' : 'text-ink-faint'}`}>
                    {action.completed ? '✓' : '○'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-sm font-sans font-medium text-ink">{action.title}</div>
                      <span className="text-[10px] uppercase tracking-wider text-ink-faint">
                        {action.priority}
                      </span>
                    </div>
                    <div className="text-xs text-ink-faint mt-1" style={{ fontWeight: 300 }}>
                      {action.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-sans font-medium text-ink-muted">
                Recent analyses
              </h2>
              {projectPhotos.length > 0 && (
                <button
                  onClick={() => navigate('/compare')}
                  className="text-xs font-sans font-medium text-rose hover:text-rose-dark transition-colors"
                >
                  Compare
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2].map(index => (
                  <div key={index} className="card flex gap-4 p-4">
                    <div className="skeleton w-10 h-10 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="skeleton h-3 w-32 rounded" />
                      <div className="skeleton h-2 w-20 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : projectPhotos.length > 0 ? (
              <div className="space-y-2">
                {projectPhotos.slice(0, 5).map(photo => {
                  const score = photo.aggregates?.[0]?.composite_score;
                  return (
                    <div
                      key={photo.id}
                      onClick={() => navigate(`/results/${photo.id}`)}
                      className="card-hover flex items-center gap-4 p-4"
                    >
                      <div className="w-10 h-10 bg-cream-warm rounded-lg flex items-center justify-center text-sm">
                        {activeConfig.emoji}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-sans font-medium text-ink">
                          {activeConfig.label}
                        </div>
                        <div className="text-[11px] text-ink-faint">{fmtDate(photo.created_at)}</div>
                      </div>
                      {score != null && (
                        <div className={`text-lg font-sans ${scoreColor(score)}`} style={{ fontWeight: 600 }}>
                          {score}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="card text-center py-10">
                <div className="text-3xl mb-3">{activeConfig.emoji}</div>
                <p className="text-sm font-sans text-ink mb-1" style={{ fontWeight: 500 }}>
                  Start your {activeConfig.shortLabel} workspace
                </p>
                <p className="text-xs text-ink-faint mb-4" style={{ fontWeight: 300 }}>
                  You have {credits} credit{credits !== 1 ? 's' : ''} available.
                </p>
                <button onClick={() => fileInputRef.current?.click()} className="btn-rose text-xs px-6 py-2.5">
                  Upload a photo
                </button>
              </div>
            )}
          </div>

          {projectOutcomeLogs.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-sans text-ink-faint uppercase tracking-wider mb-3">
                Your tracked results
              </h3>
              <div className="space-y-2">
                {projectOutcomeLogs.slice(0, 5).map((log, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-3 card">
                    <div>
                      <span className="font-medium text-ink">{log.value} {log.metric.replace('_', ' ')}</span>
                      {log.note && <span className="text-ink-faint"> · {log.note}</span>}
                    </div>
                    <span className="text-xs text-ink-faint">{fmtDate(log.loggedAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(latestRetest || latestOutcome || averageScore != null) && (
            <div className="card">
              <h2 className="text-sm font-sans font-medium text-ink-muted mb-3">
                Progress snapshot
              </h2>
              <div className="space-y-3 text-sm">
                {averageScore != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-ink-faint" style={{ fontWeight: 300 }}>
                      Average score
                    </span>
                    <span className={`font-sans font-medium ${scoreColor(averageScore)}`}>
                      {averageScore}
                    </span>
                  </div>
                )}
                {latestRetest && (
                  <div className="flex items-center justify-between">
                    <span className="text-ink-faint" style={{ fontWeight: 300 }}>
                      Latest retest
                    </span>
                    <span className="text-ink font-medium">
                      {latestRetest.change == null ? latestRetest.score : `${latestRetest.score} (${latestRetest.change >= 0 ? '+' : ''}${latestRetest.change})`}
                    </span>
                  </div>
                )}
                {latestOutcome && (
                  <div className="flex items-center justify-between">
                    <span className="text-ink-faint capitalize" style={{ fontWeight: 300 }}>
                      Latest {latestOutcome.metric.replace('_', ' ')}
                    </span>
                    <span className="text-ink font-medium">{latestOutcome.value}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
